import React, { useState, useEffect } from 'react';
import AdminDashboard from './AdminDashboard';
import { adminAPI, userAPI } from '../../services/api';

const StatCard = ({ label, value, icon, color }) => (
  <div className="bg-[var(--surface-white)] border border-[var(--border-light)] rounded-3xl p-6 shadow-xl shadow-black/5 flex items-center gap-5">
    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${color}`}>
      <span className="material-symbols-outlined text-2xl">{icon}</span>
    </div>
    <div>
      <p className="text-3xl font-black text-[var(--text-primary)]">{value ?? '—'}</p>
      <p className="text-xs text-[var(--text-secondary)] font-medium uppercase tracking-wider mt-0.5">{label}</p>
    </div>
  </div>
);

const AdminFinances = () => {
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState({
    path_a_min_seats: '10',
    path_a_seat_price: '100',
    path_b_min_coach_price: '120',
    ally_min_cut: '100',
    ally_rev_share_pct: '50',
  });
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const [refundForm, setRefundForm] = useState({ chargeId: '', amount: '', reverseTransfer: false });
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundResult, setRefundResult] = useState(null);

  const [revShareForm, setRevShareForm] = useState({ coachId: '', revShareOverride: '' });
  const [revShareLoading, setRevShareLoading] = useState(false);
  const [revShareResult, setRevShareResult] = useState(null);
  const [coaches, setCoaches] = useState([]);

  useEffect(() => {
    Promise.all([adminAPI.getStats(), adminAPI.getSettings(), userAPI.getAll({ role: 'coach' })])
      .then(([statsRes, settingsRes, coachesRes]) => {
        setStats(statsRes.data);
        setSettings((prev) => ({ ...prev, ...settingsRes.data }));
        setCoaches(Array.isArray(coachesRes.data) ? coachesRes.data : []);
      })
      .catch((err) => console.error('Failed to load admin data:', err));
  }, []);

  const handleSettingChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setSettingsDirty(true);
  };

  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true);
      await adminAPI.updateSettings(settings);
      setSettingsDirty(false);
    } catch (err) {
      alert('Failed to save settings: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingSettings(false);
    }
  };

  const handleRefund = async () => {
    if (!refundForm.chargeId.trim()) { alert('Enter a Charge ID'); return; }
    try {
      setRefundLoading(true);
      setRefundResult(null);
      const res = await adminAPI.processRefund({
        chargeId: refundForm.chargeId,
        amount: refundForm.amount ? Math.round(parseFloat(refundForm.amount) * 100) : undefined,
        reverseTransfer: refundForm.reverseTransfer,
      });
      setRefundResult({ success: true, id: res.data.refund?.id });
      setRefundForm({ chargeId: '', amount: '', reverseTransfer: false });
    } catch (err) {
      setRefundResult({ success: false, error: err.response?.data?.error || err.message });
    } finally {
      setRefundLoading(false);
    }
  };

  const handleRevShareOverride = async () => {
    if (!revShareForm.coachId.trim() || revShareForm.revShareOverride === '') {
      alert('Fill both Coach ID and override percentage');
      return;
    }
    try {
      setRevShareLoading(true);
      setRevShareResult(null);
      await adminAPI.overrideRevShare({
        coachId: revShareForm.coachId,
        revShareOverride: parseFloat(revShareForm.revShareOverride),
      });
      setRevShareResult({ success: true });
    } catch (err) {
      setRevShareResult({ success: false, error: err.response?.data?.error || err.message });
    } finally {
      setRevShareLoading(false);
    }
  };

  return (
    <AdminDashboard title="Financial Management">
      <div className="p-8 space-y-10">

        {/* Stats Row */}
        <section>
          <h2 className="text-xs font-black text-[var(--text-secondary)]/60 uppercase tracking-widest mb-4 flex items-center gap-2">
            <span className="material-symbols-outlined text-base">bar_chart</span>
            Live Stats
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <StatCard
              label="Active Path A Subscriptions"
              value={stats?.activePathASubs}
              icon="business_center"
              color="bg-blue-500/10 text-blue-500"
            />
            <StatCard
              label="Active Path B Clients"
              value={stats?.activePathBClients}
              icon="payments"
              color="bg-green-500/10 text-green-500"
            />
          </div>
        </section>

        <div className="border-t border-[var(--border-light)]" />

        {/* Pricing Settings */}
        <section>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xs font-black text-[var(--text-secondary)]/60 uppercase tracking-widest flex items-center gap-2">
              <span className="material-symbols-outlined text-base">tune</span>
              Global Pricing Settings
            </h2>
            {settingsDirty && (
              <button
                onClick={handleSaveSettings}
                disabled={savingSettings}
                className="flex items-center gap-2 px-5 py-2 bg-tangerine text-white font-bold rounded-xl shadow hover:bg-orange-600 transition-all disabled:opacity-50 text-sm"
              >
                {savingSettings ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <span className="material-symbols-outlined text-base">save</span>
                )}
                {savingSettings ? 'Saving...' : 'Save Changes'}
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { key: 'path_a_min_seats', label: 'Path A: Minimum Seats', suffix: 'seats' },
              { key: 'path_a_seat_price', label: 'Path A: Price per Seat', prefix: '$', suffix: '/month' },
              { key: 'path_b_min_coach_price', label: 'Path B: Min Coach Price', prefix: '$', suffix: '/month' },
              { key: 'ally_min_cut', label: 'Ally Min Cut (Path B)', prefix: '$' },
              { key: 'ally_rev_share_pct', label: 'Ally Rev Share (Path B)', suffix: '%' },
            ].map(({ key, label, prefix, suffix }) => (
              <div key={key} className="bg-[var(--off-white)] rounded-2xl border border-[var(--border-light)] p-4">
                <label className="block text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider mb-2">
                  {label}
                </label>
                <div className="flex items-center gap-2">
                  {prefix && <span className="text-[var(--text-secondary)] font-bold">{prefix}</span>}
                  <input
                    type="number"
                    value={settings[key] || ''}
                    onChange={(e) => handleSettingChange(key, e.target.value)}
                    className="flex-1 bg-[var(--surface-white)] border border-[var(--border-light)] text-[var(--text-primary)] font-bold rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-tangerine/30 transition-all"
                  />
                  {suffix && <span className="text-[var(--text-secondary)] font-bold text-sm whitespace-nowrap">{suffix}</span>}
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="border-t border-[var(--border-light)]" />

        {/* Refunds */}
        <section>
          <h2 className="text-xs font-black text-[var(--text-secondary)]/60 uppercase tracking-widest mb-6 flex items-center gap-2">
            <span className="material-symbols-outlined text-base">currency_exchange</span>
            Process Refund
          </h2>

          <div className="bg-[var(--off-white)] border border-[var(--border-light)] rounded-3xl p-6 space-y-4">
            <div>
              <label className="block text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider mb-2">
                Stripe Charge ID
              </label>
              <input
                type="text"
                placeholder="ch_..."
                value={refundForm.chargeId}
                onChange={(e) => setRefundForm((p) => ({ ...p, chargeId: e.target.value }))}
                className="w-full bg-[var(--surface-white)] border border-[var(--border-light)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-tangerine/30 transition-all placeholder-[var(--text-secondary)]/40"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider mb-2">
                Amount ($ — leave blank for full refund)
              </label>
              <input
                type="number"
                placeholder="e.g. 50.00"
                value={refundForm.amount}
                onChange={(e) => setRefundForm((p) => ({ ...p, amount: e.target.value }))}
                className="w-full bg-[var(--surface-white)] border border-[var(--border-light)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-tangerine/30 transition-all placeholder-[var(--text-secondary)]/40"
              />
            </div>

            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={refundForm.reverseTransfer}
                onChange={(e) => setRefundForm((p) => ({ ...p, reverseTransfer: e.target.checked }))}
                className="w-4 h-4 rounded accent-tangerine"
              />
              <span className="text-sm font-bold text-[var(--text-primary)]">Also reverse coach's transfer</span>
              <span className="text-xs text-[var(--text-secondary)]">(only for Path B payments)</span>
            </label>

            <div className="flex items-center gap-3 pt-1">
              <button
                onClick={handleRefund}
                disabled={refundLoading}
                className="flex items-center gap-2 px-6 py-2.5 bg-red-500 hover:bg-red-600 text-white font-bold rounded-xl shadow transition-all disabled:opacity-50 text-sm"
              >
                {refundLoading ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <span className="material-symbols-outlined text-base">undo</span>
                )}
                {refundLoading ? 'Processing...' : 'Issue Refund'}
              </button>

              {refundResult && (
                <div className={`flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-xl ${refundResult.success ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}`}>
                  <span className="material-symbols-outlined text-base">
                    {refundResult.success ? 'check_circle' : 'error'}
                  </span>
                  {refundResult.success ? `Refund issued: ${refundResult.id}` : refundResult.error}
                </div>
              )}
            </div>
          </div>
        </section>

        <div className="border-t border-[var(--border-light)]" />

        {/* Rev Share Override */}
        <section>
          <h2 className="text-xs font-black text-[var(--text-secondary)]/60 uppercase tracking-widest mb-6 flex items-center gap-2">
            <span className="material-symbols-outlined text-base">percent</span>
            Coach Revenue Share Override
          </h2>
          <p className="text-sm text-[var(--text-secondary)] mb-4 leading-relaxed">
            Override the standard revenue-split formula for a specific coach. Normally Ally takes <strong>MAX($100, 50% × price)</strong>. Setting an override percentage changes the 50% portion only for this coach.
          </p>

          <div className="bg-[var(--off-white)] border border-[var(--border-light)] rounded-3xl p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider mb-2">
                  Select Coach
                </label>
                <select
                  value={revShareForm.coachId}
                  onChange={(e) => setRevShareForm((p) => ({ ...p, coachId: e.target.value }))}
                  className="w-full bg-[var(--surface-white)] border border-[var(--border-light)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-tangerine/30 transition-all"
                >
                  <option value="">— Choose a coach —</option>
                  {coaches.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.email})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider mb-2">
                  Ally's Share (%) — e.g. 40 means 40%
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  placeholder="50"
                  value={revShareForm.revShareOverride}
                  onChange={(e) => setRevShareForm((p) => ({ ...p, revShareOverride: e.target.value }))}
                  className="w-full bg-[var(--surface-white)] border border-[var(--border-light)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-tangerine/30 transition-all placeholder-[var(--text-secondary)]/40"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleRevShareOverride}
                disabled={revShareLoading}
                className="flex items-center gap-2 px-6 py-2.5 bg-tangerine hover:bg-orange-600 text-white font-bold rounded-xl shadow transition-all disabled:opacity-50 text-sm"
              >
                {revShareLoading ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <span className="material-symbols-outlined text-base">edit</span>
                )}
                {revShareLoading ? 'Saving...' : 'Apply Override'}
              </button>

              {revShareResult && (
                <div className={`flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-xl ${revShareResult.success ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}`}>
                  <span className="material-symbols-outlined text-base">
                    {revShareResult.success ? 'check_circle' : 'error'}
                  </span>
                  {revShareResult.success ? 'Override applied!' : revShareResult.error}
                </div>
              )}
            </div>
          </div>
        </section>

        <div className="border-t border-[var(--border-light)]" />

        {/* Coach Subscriptions (Path A) */}
        <section>
          <h2 className="text-xs font-black text-[var(--text-secondary)]/60 uppercase tracking-widest mb-6 flex items-center gap-2">
            <span className="material-symbols-outlined text-base">business_center</span>
            Coach Subscriptions (Path A)
          </h2>
          <div className="bg-[var(--surface-white)] border border-[var(--border-light)] shadow-xl shadow-black/5 rounded-3xl overflow-hidden">
            {!stats?.pathASubsDetails || stats.pathASubsDetails.length === 0 ? (
              <div className="text-center py-8 text-sm text-[var(--text-secondary)]">
                No Path A subscriptions found.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-[var(--border-light)]/40 text-sm">
                  <thead>
                    <tr className="bg-[var(--off-white)] text-left text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider">
                      <th className="px-6 py-4">Coach</th>
                      <th className="px-6 py-4">Stripe Subscription ID</th>
                      <th className="px-6 py-4 text-center">Seats</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4">Subscribed On</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--border-light)]/30 font-medium">
                    {stats.pathASubsDetails.map((sub) => (
                      <tr key={sub.id} className="hover:bg-[var(--off-white)]/30 transition-colors">
                        <td className="px-6 py-4">
                          <p className="font-bold text-[var(--text-primary)]">{sub.coachName}</p>
                          <p className="text-xs text-[var(--text-secondary)]">{sub.coachEmail}</p>
                        </td>
                        <td className="px-6 py-4 font-mono text-xs text-[var(--text-secondary)]">
                          {sub.stripeSubscriptionId}
                        </td>
                        <td className="px-6 py-4 text-center font-bold text-[var(--text-primary)]">
                          {sub.seats}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full ${
                            sub.status === 'active' ? 'text-green-500 bg-green-500/10 border border-green-500/20' : 'text-orange-500 bg-orange-500/10 border border-orange-500/20'
                          }`}>
                            {sub.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-xs text-[var(--text-secondary)]">
                          {new Date(sub.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        <div className="border-t border-[var(--border-light)]" />

        {/* Client Subscriptions (Path B) */}
        <section>
          <h2 className="text-xs font-black text-[var(--text-secondary)]/60 uppercase tracking-widest mb-6 flex items-center gap-2">
            <span className="material-symbols-outlined text-base">payments</span>
            Client Subscriptions (Path B)
          </h2>
          <div className="bg-[var(--surface-white)] border border-[var(--border-light)] shadow-xl shadow-black/5 rounded-3xl overflow-hidden">
            {!stats?.pathBSubsDetails || stats.pathBSubsDetails.length === 0 ? (
              <div className="text-center py-8 text-sm text-[var(--text-secondary)]">
                No Path B client subscriptions found.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-[var(--border-light)]/40 text-sm">
                  <thead>
                    <tr className="bg-[var(--off-white)] text-left text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider">
                      <th className="px-6 py-4">Client</th>
                      <th className="px-6 py-4">Coach</th>
                      <th className="px-6 py-4">Stripe Subscription ID</th>
                      <th className="px-6 py-4 text-right">Price</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4">Subscribed On</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--border-light)]/30 font-medium">
                    {stats.pathBSubsDetails.map((rel) => (
                      <tr key={rel.id} className="hover:bg-[var(--off-white)]/30 transition-colors">
                        <td className="px-6 py-4">
                          <p className="font-bold text-[var(--text-primary)]">{rel.clientName}</p>
                          <p className="text-xs text-[var(--text-secondary)]">{rel.clientEmail}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="font-bold text-[var(--text-primary)]">{rel.coachName}</p>
                          <p className="text-xs text-[var(--text-secondary)]">{rel.coachEmail}</p>
                        </td>
                        <td className="px-6 py-4 font-mono text-xs text-[var(--text-secondary)]">
                          {rel.stripeSubscriptionId || '—'}
                        </td>
                        <td className="px-6 py-4 text-right font-black text-tangerine">
                          ${rel.price ? rel.price.toFixed(2) : '0.00'}/mo
                        </td>
                        <td className="px-6 py-4">
                          <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full ${
                            rel.status === 'active' ? 'text-green-500 bg-green-500/10 border border-green-500/20' : 
                            rel.status === 'pending' ? 'text-yellow-600 bg-yellow-500/10 border border-yellow-500/20' :
                            'text-red-500 bg-red-500/10 border border-red-500/20'
                          }`}>
                            {rel.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-xs text-[var(--text-secondary)]">
                          {new Date(rel.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

      </div>
    </AdminDashboard>
  );
};

export default AdminFinances;
